"use client";

import { useMemo } from "react";
import { useRouter } from "@/lib/navigation";
import {
  clipWebMcpText,
  requireWebMcpString,
  useWebMcpTools,
  webMcpInteger,
} from "@/hooks/use-webmcp-tools";

type NotesFeedToolItem = {
  stableKey?: unknown;
  external?: unknown;
  publishedAt?: unknown;
  slug?: unknown;
  sourceName?: unknown;
  title?: unknown;
  url?: unknown;
};

export function AtlasNotesWebMcp({ enabled }: { enabled: boolean }) {
  const router = useRouter();
  const tools = useMemo(
    () => [
      {
        name: "atlas_search_notes",
        title: "Search Atlas Notes",
        description:
          "Searches the signed-in member's CS Atlas Notes feed and opens the matching visible feed. Returns a bounded list of note titles and links.",
        inputSchema: {
          type: "object",
          properties: {
            query: {
              type: "string",
              description: "Words, company, author, or topic to find in Atlas Notes.",
              minLength: 1,
              maxLength: 200,
            },
            sort: {
              type: "string",
              description:
                "Order matches by publication date, or explicitly by relevance.",
              enum: ["newest", "oldest", "relevance"],
              default: "newest",
            },
            limit: {
              type: "integer",
              description: "Number of compact matches to return, from 1 to 5.",
              minimum: 1,
              maximum: 5,
              default: 5,
            },
          },
          required: ["query"],
          additionalProperties: false,
        },
        annotations: {
          readOnlyHint: true,
          untrustedContentHint: true,
        },
        execute: async (input: Record<string, unknown>) => {
          const query = requireWebMcpString(input, "query", { maxLength: 200 });
          const sort = input.sort === undefined ? "newest" : input.sort;
          if (sort !== "newest" && sort !== "oldest" && sort !== "relevance") {
            throw new Error("sort must be newest, oldest or relevance.");
          }
          const limit = webMcpInteger(input, "limit", {
            defaultValue: 5,
            min: 1,
            max: 5,
          });
          const params = new URLSearchParams({ q: query, sort });
          const items: NotesFeedToolItem[] = [];
          let nextCursor: string | null = null;
          // Chronological search can yield an empty scan before older matches.
          // Follow bounded pages rather than reporting that no notes exist.
          for (let scan = 0; scan < 10 && items.length < limit; scan++) {
            const pageParams = new URLSearchParams(params);
            pageParams.set("limit", String(limit - items.length));
            if (nextCursor) pageParams.set("cursor", nextCursor);
            const response = await fetch(
              `/api/notes/feed?${pageParams.toString()}`,
              {
                cache: "no-store",
                credentials: "same-origin",
                headers: { Accept: "application/json" },
              },
            );
            if (!response.ok)
              throw new Error(
                await webMcpResponseError(response, "Unable to search Notes."),
              );
            const body = (await response.json()) as {
              items?: NotesFeedToolItem[];
              nextCursor?: string | null;
            };
            if (Array.isArray(body.items)) items.push(...body.items);
            const previousCursor: string | null = nextCursor;
            nextCursor =
              typeof body.nextCursor === "string" && body.nextCursor
                ? body.nextCursor
                : null;
            if (!nextCursor || nextCursor === previousCursor) break;
          }
          const results = items.length
            ? items.slice(0, limit).map((item) => {
                const slug = typeof item.slug === "string" ? item.slug : "";
                const external = item.external === true;
                const href =
                  external && typeof item.url === "string"
                    ? item.url
                    : slug
                      ? `/notes?${new URLSearchParams({ note: slug, q: query, sort }).toString()}`
                      : "/notes";
                return {
                  title: clipWebMcpText(item.title, 120),
                  source: clipWebMcpText(item.sourceName, 60),
                  published_at:
                    typeof item.publishedAt === "number"
                      ? new Date(item.publishedAt).toISOString().slice(0, 10)
                      : null,
                  href,
                };
              })
            : [];
          router.push(`/notes?${params.toString()}`);
          return {
            ok: true,
            query,
            sort,
            visible_feed: `/notes?${params.toString()}`,
            has_more: Boolean(nextCursor),
            results,
          };
        },
      },
    ],
    [router],
  );

  useWebMcpTools(enabled, tools);
  return null;
}

async function webMcpResponseError(response: Response, fallback: string) {
  try {
    const body = (await response.json()) as { error?: unknown };
    return typeof body.error === "string"
      ? clipWebMcpText(body.error, 200)
      : fallback;
  } catch {
    return fallback;
  }
}
